<template>
  <div>
    <div v-if="!loading" class="profile">
      <Subheader :title="`${userBeingViewed.username}'s Profile`" />

      <div class="app-content-column">
        <div class="profile__card">
          <div class="profile__top">
            <UserProfileImage
              :profile-pic="userBeingViewed.profilePic"
              class="profile__user-image"
            />
            <div class="profile__info">
              <!-- Username -->
              <div v-if="userBeingViewed.username !== ''" class="profile__username">
                {{ userBeingViewed.username ? userBeingViewed.username : 'No Name Provided' }}
              </div>

              <!-- User Status -->
              <div class="profile__status">Status: {{ userStatus }}</div>
            </div>
          </div>
          <!-- Follow Button -->
          <div v-if="!viewingMyProfile">
            <div>
              <LoadingSpinnerButton
                class="medium-button small-button--inverted"
                :loading="loadingFollow"
                :text="followedByMe ? 'Unfollow' : 'Follow'"
                @click="toggleFollowUser"
              />
            </div>
          </div>
          <div style="margin-top: 14px;">
            <!-- User Interests -->
            <div class="profile__bold-text">Causes, Location & Interests</div>
            <div v-if="userInterests.length" class="button-wrapper profile__interests">
              <div v-for="interest in userInterests" :key="interest.id" class="tag">
                {{ interest.name }}
              </div>
              <div v-if="!userInterests.length" class="profile__text">N/A</div>
            </div>
          </div>
        </div>

        <div class="profile__card">
          <!-- User Bio -->
          <div class="profile__bold-text">About</div>
          <div v-if="userBeingViewed.description.length" class="profile__text">
            {{ userBeingViewed.description }}
          </div>
          <div v-if="!userBeingViewed.description.length" class="profile__text">N/A</div>

          <!-- User Social Media -->
          <div style="margin-top: 1rem;">
            <div v-if="userBeingViewed.facebook" class="social">
              <font-awesome-icon :icon="['fab', 'facebook']" size="2x" class="social__icon" />
              <a :href="userBeingViewed.facebook" class="social__text">
                {{ userBeingViewed.facebook }}
              </a>
            </div>
            <div v-if="userBeingViewed.instagram" class="social">
              <font-awesome-icon :icon="['fab', 'instagram']" size="2x" class="social__icon" />
              <a :href="userBeingViewed.instagram" class="social__text">
                {{ userBeingViewed.instagram }}
              </a>
            </div>
            <div v-if="userBeingViewed.twitter" class="social">
              <font-awesome-icon :icon="['fab', 'twitter']" size="2x" class="social__icon" />
              <a :href="userBeingViewed.twitter" class="social__text">
                {{ userBeingViewed.twitter }}
              </a>
            </div>
          </div>
        </div>

        <!-- Projects that user created -->
        <div
          v-if="userBeingViewed.isMaker && userBeingViewed.mVerifiedProjects.length"
          class="profile__card"
        >
          <div class="profile__bold-text">Projects I Created</div>
          <ProjectSmallCard
            v-for="project in userBeingViewed.mVerifiedProjects"
            :key="project.id"
            :project="project"
          />
        </div>

        <!-- Projects that user is following -->
        <div v-if="userBeingViewed.followingProjectsRef.length" class="profile__card">
          <div class="profile__bold-text">Projects I'm Following</div>
          <ProjectSmallCard
            v-for="project in userBeingViewed.followingProjectsRef"
            :key="project.id"
            :project="project"
          />
        </div>

        <!-- Projects that user verified -->
        <div v-if="userBeingViewed.approvedProjectsRef.length" class="profile__card">
          <div class="profile__bold-text">Projects I Verified</div>
          <ProjectSmallCard
            v-for="project in userBeingViewed.approvedProjectsRef"
            :key="project.id"
            :project="project"
          />
        </div>

        <!-- Following -->
        <div v-if="userBeingViewed.followingUsersRef.length" class="profile__card">
          <div class="profile__bold-text">Users I'm Following</div>
          <div
            v-for="followingUser in userBeingViewed.followingUsersRef"
            :key="followingUser.id"
            class="user-small-card"
            @click="routeToUserProfile(followingUser.id)"
          >
            <UserProfileImage
              :profile-pic="followingUser.profilePic"
              class="user-small-card__img"
              width="50px"
              height="50px"
            />
            <div class="user-small-card__username">{{ followingUser.username }}</div>
          </div>
        </div>
      </div>
    </div>

    <Spinner v-if="loading" />
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

import User from '@/services/users'
import Attribute from '@/services/attributes'
import { errorMessage } from '@/services/utils/apiErrors'

import Spinner from '@/components/Spinner'
import LoadingSpinnerButton from '@/components/buttons/LoadingSpinnerButton'
import Subheader from '@/components/Subheader'
import UserProfileImage from '@/components/images/UserProfileImage'
import ProjectSmallCard from '@/components/ProjectSmallCard'

export default {
  name: 'Profile',
  components: {
    Spinner,
    LoadingSpinnerButton,
    Subheader,
    UserProfileImage,
    ProjectSmallCard,
  },
  data() {
    return {
      userBeingViewed: User.create(),
      userInterests: [],
      loading: false,
      loadingFollow: false,
    }
  },
  computed: {
    ...mapGetters(['userIsLoggedIn']),
    /**
     * Add Change Funder status if the user does not have one of these specific roles: Activator, Maker, Staff.
     * If user does have 1+ roles, don't show Change Funder status.
     */
    userStatus() {
      const statuses = []
      this.userBeingViewed.isActivator ? statuses.push('Activator') : 0
      this.userBeingViewed.isMaker ? statuses.push('Change Maker') : 0
      this.userBeingViewed.isStaff ? statuses.push('MzingoP artners Staff') : 0
      !statuses.length ? statuses.push('Mzingo Partners') : 0
      return statuses.join(', ')
    },
    viewingMyProfile() {
      if (!this.$store.state.user) return false
      return this.userBeingViewed.id == this.$store.state.user.id
    },
    followedByMe() {
      if (!this.$store.state.user) return false
      return this.$store.state.user.followingUsers.includes(this.userBeingViewed.id)
    },
  },
  async created() {
    this.userBeingViewed.id = this.$route.params.userId
    this.refreshUser()
  },
  methods: {
    async refreshUser() {
      this.loading = true
      try {
        const userResponse = await User.api.getUser(this.userBeingViewed.id)
        this.userBeingViewed = userResponse
        await this.getUserInterests()
      } catch {
        errorMessage()
      } finally {
        this.loading = false
      }
    },
    async getUserInterests() {
      try {
        let copy = [...this.userBeingViewed.interests]
        let attrList = await Attribute.api.list()
        let selectedAttrs = []
        attrList.results.map(attr => (copy.includes(attr.id) ? selectedAttrs.push(attr) : null))
        this.userInterests = selectedAttrs
      } catch {
        errorMessage()
      }
    },
    routeToUserProfile(userId) {
      this.$router.push({ name: 'Profile', params: { userId: userId } })
    },
    async toggleFollowUser() {
      if (!this.userIsLoggedIn) {
        this.$router.push({ name: 'Login' })
        return
      }

      try {
        this.loadingFollow = true
        const fields = ['followingUsers']
        let user = User.create(this.$store.state.user)
        if (this.followedByMe) {
          user.followingUsers = this.$store.state.user.followingUsers.filter(
            u => u !== this.userBeingViewed.id,
          )
        } else {
          user.followingUsers.push(this.userBeingViewed.id)
        }

        const response = await User.api.update(user, fields)
        this.$store.commit('UPDATE_USER', response)
        await this.refreshUser()
      } catch {
        errorMessage()
      } finally {
        this.loadingFollow = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import '../styles/variables';
@import '../styles/layout';
@import '../styles/mixins/buttons';

.profile {
  text-align: center;
  padding-bottom: 2rem;

  &__card {
    display: inline-block;
    width: 90%;
    margin: 5px auto;
    padding: 1rem;
    border-radius: 8px;
    box-shadow: 0 20px 20px 0 rgba(0, 0, 0, 0.08);
    background-color: white;
  }

  &__top {
    display: flex;
  }

  &__info {
    margin: 2rem 1rem;
    text-align: left;
    display: flex;
    flex-direction: column;
    justify-content: space-evenly;
  }

  &__user-image {
    margin-top: 80px;
  }

  &__username {
    font-size: 18px;
    font-weight: 900;
    margin-bottom: 6px;
  }

  &__status {
    font-size: 14px;
    line-height: normal;
    color: $gray;
    display: flex;
    align-items: center;
  }

  &__interests {
    margin: 4px auto;
  }

  &__text {
    font-size: 14px;
    line-height: 1.2rem;
    color: #000000;
    text-align: left;
  }

  &__bold-text {
    text-align: left;
    font-weight: 700;
    margin: 4px auto 10px auto;
  }
}

.social {
  display: flex;
  align-items: center;
  margin: 0.5rem 0;

  &__icon {
    flex: 1;
    margin-right: 0.5rem;
    color: $primary;
  }

  &__text {
    flex: 10;
    font-weight: 200;
    text-align: left;
    font-size: 14px;
    line-height: 1.2rem;
    color: #000000;
    text-align: left;
  }
}

.user-small-card {
  display: flex;
  align-items: center;
  margin-left: 10px;
  cursor: pointer;

  &__img {
    margin-top: 50px;
  }
  &__username {
    margin-left: 12px;
  }
}
</style>
